import { useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Bookmark, BookmarkPlus, Globe, Save, Star, Users2, X } from "lucide-react";
import { toast } from "sonner";

export const SegmentsSidebar = ({
  segments, user, canBulk, filters, activeSegmentId,
  onApplySegment, onClearFilters, reloadSegments,
}) => {
  const [showSaveSegment, setShowSaveSegment] = useState(false);
  const [segmentForm, setSegmentForm] = useState({ name: "", share_scope: "private" });

  const saveSegment = async () => {
    if (!segmentForm.name.trim()) return toast.error("Name required");
    try {
      const payload = {
        name: segmentForm.name.trim(),
        share_scope: segmentForm.share_scope,
        filter_config: {
          tags: filters.tags,
          language: filters.language,
          has_email: filters.has_email,
          has_open_tickets: filters.has_open_tickets,
          q: filters.q,
          sort: filters.sort,
          dir: filters.dir,
        },
      };
      await api.post("/customer-segments", payload);
      toast.success("Segment saved");
      setShowSaveSegment(false);
      setSegmentForm({ name: "", share_scope: "private" });
      reloadSegments();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const deleteSegment = async (seg) => {
    if (!window.confirm(`Delete segment "${seg.name}"?`)) return;
    try {
      await api.delete(`/customer-segments/${seg.id}`);
      toast.success("Segment deleted");
      reloadSegments();
      if (activeSegmentId === seg.id) onClearFilters();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <>
      <Card className="border-slate-200 h-fit" data-testid="segments-sidebar">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-sm uppercase tracking-wider text-slate-500 flex items-center justify-between">
            <span className="flex items-center gap-1.5"><Bookmark className="w-3.5 h-3.5" /> Segments</span>
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setShowSaveSegment(true)}
                    title="Save current filter as segment"
                    data-testid="save-segment-btn">
              <BookmarkPlus className="w-3.5 h-3.5 text-indigo-600" />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="-mt-2 px-2 pb-3 space-y-1">
          <button
            type="button"
            onClick={onClearFilters}
            className={`w-full text-left px-2 py-1.5 rounded text-xs flex items-center gap-2 ${
              !activeSegmentId ? "bg-indigo-50 text-indigo-700 font-medium" : "hover:bg-slate-100 text-slate-700"
            }`}
            data-testid="segment-all"
          >
            <Users2 className="w-3.5 h-3.5" /> All customers
          </button>
          {segments.map((s) => (
            <div key={s.id} className="group flex items-center gap-1">
              <button
                type="button"
                onClick={() => onApplySegment(s)}
                className={`flex-1 text-left px-2 py-1.5 rounded text-xs flex items-center gap-2 ${
                  activeSegmentId === s.id ? "bg-indigo-50 text-indigo-700 font-medium" : "hover:bg-slate-100 text-slate-700"
                }`}
                data-testid={`segment-${s.id}`}
              >
                {s.name === "VIP" ? <Star className="w-3.5 h-3.5 text-amber-500" /> : <Bookmark className="w-3.5 h-3.5" />}
                <span className="flex-1 truncate">{s.name}</span>
                {s.share_scope === "org" && (
                  <Globe className="w-2.5 h-2.5 text-slate-400" title="Shared with org" />
                )}
              </button>
              {!s.is_seed && (s.owner_email === user?.email || user?.is_super_admin) && (
                <button type="button" onClick={() => deleteSegment(s)}
                        className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-600 px-1"
                        title="Delete segment"
                        data-testid={`segment-delete-${s.id}`}>
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      <Dialog open={showSaveSegment} onOpenChange={setShowSaveSegment}>
        <DialogContent data-testid="save-segment-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Save current filter as segment</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Save the active tags / language / open-ticket filters under a name you can apply with one click later.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Segment name</Label>
              <Input value={segmentForm.name} onChange={(e) => setSegmentForm({ ...segmentForm, name: e.target.value })}
                     placeholder="e.g. Arabic VIPs with open tickets" data-testid="segment-name-input" />
            </div>
            {canBulk && (
              <div className="flex items-center gap-3">
                <Switch checked={segmentForm.share_scope === "org"}
                        onCheckedChange={(v) => setSegmentForm({ ...segmentForm, share_scope: v ? "org" : "private" })}
                        data-testid="segment-org-toggle" />
                <Label className="text-xs">Share with org (visible to all agents)</Label>
              </div>
            )}
            <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded p-2 space-y-0.5">
              <div className="font-semibold uppercase tracking-wider text-slate-400 text-[10px]">Filter snapshot</div>
              {filters.tags.length > 0 && <div>tags: {filters.tags.join(", ")}</div>}
              {filters.language !== "all" && <div>language: {filters.language}</div>}
              {filters.has_email !== "any" && <div>has_email: {filters.has_email}</div>}
              {filters.has_open_tickets !== "any" && <div>has_open_tickets: {filters.has_open_tickets}</div>}
              {filters.q && <div>q: {filters.q}</div>}
              {!filters.tags.length && filters.language === "all" && filters.has_email === "any"
                && filters.has_open_tickets === "any" && !filters.q && (
                  <div className="text-slate-400">No filters active — segment will match everyone.</div>
                )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveSegment(false)}>Cancel</Button>
            <Button onClick={saveSegment} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-segment-confirm-btn">
              <Save className="w-3.5 h-3.5 mr-1.5" /> Save segment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
